<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h6 class="mb-0">{{ isEditing ? 'Editar Promoción' : 'Agregar Promoción' }}</h6>
              <div class="d-flex gap-2">
                <router-link :to="{ name: 'Promotions' }" class="btn btn-outline-secondary btn-sm mb-0">
                  <i class="fa fa-arrow-left me-1"></i> Volver
                </router-link>
                <button type="button" class="btn btn-primary btn-sm mb-0" :disabled="isSaving || isLoading" @click="submitForm">
                  <i class="fa fa-save me-1"></i> {{ isSaving ? 'Guardando...' : 'Guardar' }}
                </button>
              </div>
            </div>
          </div>
          <div class="card-body pt-2">
            <div v-if="isLoading" class="text-center text-sm text-muted py-4">Cargando promoción...</div>
            <form v-else @submit.prevent="submitForm">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-control-label">Nombre del descuento <span class="text-danger">*</span></label>
                  <input type="text" v-model="form.name" class="form-control" placeholder="Ej: Black Friday" required>
                </div>
                <div class="col-md-2 mb-3">
                  <label class="form-control-label">Descuento (%) <span class="text-danger">*</span></label>
                  <input type="number" v-model.number="form.discount_percent" class="form-control"
                    min="0.01" max="100" step="0.01" placeholder="0" required>
                </div>
                <div class="col-md-2 mb-3">
                  <label class="form-control-label">Desde <span class="text-danger">*</span></label>
                  <input type="date" v-model="form.starts_at" class="form-control" required>
                </div>
                <div class="col-md-2 mb-3">
                  <label class="form-control-label">Hasta <span class="text-danger">*</span></label>
                  <input type="date" v-model="form.ends_at" :min="form.starts_at" class="form-control" required>
                </div>
                <div class="col-md-10 mb-3">
                  <label class="form-control-label">Descripción</label>
                  <input type="text" v-model="form.description" class="form-control"
                    placeholder="Se muestra en el catálogo (opcional)">
                </div>
                <div class="col-md-2 mb-3 d-flex align-items-end">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="promoActive" v-model="form.is_active">
                    <label class="form-check-label" for="promoActive">Activa</label>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div v-if="!isLoading" class="row">
          <!-- Buscador de productos -->
          <div class="col-lg-7 mb-4">
            <div class="card h-100">
              <div class="card-header pb-0">
                <h6 class="mb-2">Agregar productos</h6>
                <div class="row g-2 mb-2">
                  <div class="col-7">
                    <input type="text" v-model="productSearch" class="form-control form-control-sm"
                      placeholder="Buscar por nombre, referencia o código..." @input="onProductSearch" />
                  </div>
                  <div class="col-5">
                    <select v-model="productCategory" class="form-select form-select-sm" @change="searchProducts">
                      <option value="">Todas las categorías</option>
                      <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-xs text-muted">
                    {{ isSearching ? 'Buscando...' : `${searchResults.length} resultado(s)` }}
                  </span>
                  <button type="button" class="btn btn-link btn-sm p-0 mb-0" :disabled="!pendingResults.length"
                    @click="addAllResults">
                    Agregar todos los resultados ({{ pendingResults.length }})
                  </button>
                </div>
              </div>
              <div class="card-body pt-2">
                <ul class="product-list">
                  <li v-for="p in searchResults" :key="p.id" class="product-item" :class="{ selected: isSelected(p.id) }"
                    @click="toggleProduct(p)">
                    <input type="checkbox" class="product-check" :checked="isSelected(p.id)" @click.stop="toggleProduct(p)" />
                    <img v-if="p.image_url" :src="p.image_url" :alt="p.name" class="product-img" />
                    <div v-else class="product-img product-img--empty"><i class="fa fa-image"></i></div>
                    <div class="flex-grow-1 text-sm">
                      <div class="font-weight-bold">{{ p.name }}</div>
                      <div class="text-xs text-muted">
                        {{ p.ref || '-' }}<span v-if="p.category"> · {{ p.category.name }}</span>
                      </div>
                    </div>
                    <div class="text-xs text-end">
                      <div>{{ formatCop(p.retail_price) }}</div>
                      <div v-if="form.discount_percent > 0" class="text-success font-weight-bold">
                        {{ formatCop(discounted(p.retail_price)) }}
                      </div>
                    </div>
                  </li>
                  <li v-if="!isSearching && !searchResults.length" class="text-center text-xs text-muted py-3">
                    No se encontraron productos
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Productos seleccionados -->
          <div class="col-lg-5 mb-4">
            <div class="card h-100">
              <div class="card-header pb-0">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">Productos en la promoción ({{ form.products.length }})</h6>
                  <button v-if="form.products.length" type="button" class="btn btn-link btn-sm text-danger p-0 mb-0"
                    @click="form.products = []">
                    Quitar todos
                  </button>
                </div>
              </div>
              <div class="card-body pt-2">
                <ul class="product-list">
                  <li v-for="p in form.products" :key="p.id" class="product-item">
                    <img v-if="p.image_url" :src="p.image_url" :alt="p.name" class="product-img" />
                    <div v-else class="product-img product-img--empty"><i class="fa fa-image"></i></div>
                    <div class="flex-grow-1 text-sm">
                      <div class="font-weight-bold">{{ p.name }}</div>
                      <div class="text-xs text-muted">{{ p.ref || '-' }}</div>
                    </div>
                    <a class="text-secondary cursor-pointer ms-2" title="Quitar" @click="removeProduct(p.id)">
                      <i class="fa fa-times"></i>
                    </a>
                  </li>
                  <li v-if="!form.products.length" class="text-center text-xs text-muted py-3">
                    Selecciona productos de la lista
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: "PromotionsCreate",
  data() {
    return {
      form: {
        id: 0,
        name: '',
        description: '',
        discount_percent: null,
        starts_at: '',
        ends_at: '',
        is_active: true,
        products: [],
      },
      isLoading: false,
      isSaving: false,
      categories: [],
      productSearch: '',
      productCategory: '',
      searchResults: [],
      isSearching: false,
      searchTimer: null,
    };
  },
  computed: {
    isEditing() {
      return !!this.$route.params.id;
    },
    selectedIds() {
      return new Set(this.form.products.map((p) => p.id));
    },
    pendingResults() {
      return this.searchResults.filter((p) => !this.selectedIds.has(p.id));
    },
  },
  mounted() {
    this.loadCategories();
    this.searchProducts();
    if (this.isEditing) this.loadPromotion();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async loadPromotion() {
      this.isLoading = true;
      try {
        const res = await this.$store.dispatch('promotions/getById', this.$route.params.id);
        const data = res.data;
        this.form = {
          id: data.id,
          name: data.name,
          description: data.description || '',
          discount_percent: data.discount_percent,
          starts_at: String(data.starts_at).substring(0, 10),
          ends_at: String(data.ends_at).substring(0, 10),
          is_active: !!data.is_active,
          products: data.products || [],
        };
      } catch (e) {
        await Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar la promoción' });
        this.$router.push({ name: 'Promotions' });
      } finally {
        this.isLoading = false;
      }
    },
    async loadCategories() {
      try {
        const res = await this.$store.dispatch('categories/getCategories');
        this.categories = res?.data || [];
      } catch (e) {
        this.categories = [];
      }
    },
    onProductSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.searchProducts, 300);
    },
    async searchProducts() {
      this.isSearching = true;
      try {
        const res = await this.$store.dispatch('promotions/searchProducts', {
          search: this.productSearch,
          category_id: this.productCategory || undefined,
          limit: 100,
        });
        this.searchResults = res.data || [];
      } catch (e) {
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },
    isSelected(id) {
      return this.selectedIds.has(id);
    },
    toggleProduct(p) {
      if (this.isSelected(p.id)) this.removeProduct(p.id);
      else this.form.products.push(p);
    },
    removeProduct(id) {
      this.form.products = this.form.products.filter((p) => p.id !== id);
    },
    addAllResults() {
      this.form.products.push(...this.pendingResults);
    },
    discounted(price) {
      const pct = parseFloat(this.form.discount_percent) || 0;
      return (parseFloat(price) || 0) * (1 - pct / 100);
    },
    formatCop(value) {
      return '$' + Math.round(parseFloat(value) || 0).toLocaleString('es-CO');
    },
    errorMessage(e, fallback) {
      const errors = e?.data?.errors;
      if (errors) return Object.values(errors).flat()[0];
      return e?.data?.message || e?.message || fallback;
    },
    async submitForm() {
      if (!this.form.name || !this.form.discount_percent || !this.form.starts_at || !this.form.ends_at) {
        Swal.fire({ icon: 'warning', title: 'Faltan datos', text: 'Completa nombre, descuento y fechas.' });
        return;
      }
      if (!this.form.products.length) {
        Swal.fire({ icon: 'warning', title: 'Faltan productos', text: 'Selecciona al menos un producto.' });
        return;
      }
      this.isSaving = true;
      try {
        const { products, ...rest } = this.form;
        await this.$store.dispatch("promotions/save", { ...rest, product_ids: products.map((p) => p.id) });
        await Swal.fire({ icon: 'success', title: '¡Éxito!', text: 'Promoción guardada correctamente.', confirmButtonText: 'Aceptar' });
        this.$router.push({ name: 'Promotions' });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.errorMessage(e, 'Error al guardar') });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.product-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 480px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
}

.product-item:last-child {
  border-bottom: none;
}

.product-item:hover {
  background: #f8f9fa;
}

.product-item.selected {
  background: #e8f5e9;
}

.product-check {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}

.product-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 0.375rem;
  flex-shrink: 0;
}

.product-img--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f3f5;
  color: #adb5bd;
}
</style>
